import {
  ECOMMERCE_FIELDS,
  type EcommerceInputs
} from "../../calculators/ecommerce/defaults";
import { ecommerceHelpHtml } from "../../calculators/ecommerce/help";
import { calculateEcommerceRoi } from "../../calculators/ecommerce/formula";
import { formatDays, formatWholeCurrency, formatNumber } from "../../lib/currency";
import type { CurrencyCode, PartnerBrand } from "../../lib/types";
import { renderFormField } from "../components/formField";
import { renderHeader } from "../components/header";
import { renderHelpModal } from "../components/helpModal";

interface EcommercePageArgs {
  inputs: EcommerceInputs;
  currency: CurrencyCode;
  brand: PartnerBrand;
  helpOpen: boolean;
  animateEntrance: boolean;
}

export function renderEcommercePage({
  inputs,
  currency,
  brand,
  helpOpen,
  animateEntrance
}: EcommercePageArgs): string {
  const result = calculateEcommerceRoi(inputs);

  const midpoint = Math.ceil(ECOMMERCE_FIELDS.length / 2);
  const leftFields = ECOMMERCE_FIELDS.slice(0, midpoint);
  const rightFields = ECOMMERCE_FIELDS.slice(midpoint);

  const renderFields = (fields: typeof leftFields) =>
    fields
      .map((field) =>
        renderFormField({
          name: field.key,
          label: field.label,
          kind: field.kind,
          value: inputs[field.key],
          currency,
          step: field.step
        })
      )
      .join("");

  return `
    <div class="roi-app ${animateEntrance ? "is-entering" : ""}">
      ${renderHeader(brand, "eCommerce ROI Calculator")}
      <main class="roi-main">
      <section class="input-section two-column" aria-labelledby="input-assumptions-title">
        <h2 id="input-assumptions-title" class="section-title">Input Assumptions</h2>
        <div class="field-column">${renderFields(leftFields)}</div>
        <div class="field-column">${renderFields(rightFields)}</div>
        <div class="currency-row align-end">
          <label for="currency">Currency</label>
          <select id="currency" name="currency" data-action="currency-change">
            <option value="BMD" ${currency === "BMD" ? "selected" : ""}>BMD</option>
            <option value="USD" ${currency === "USD" ? "selected" : ""}>USD</option>
            <option value="CAD" ${currency === "CAD" ? "selected" : ""}>CAD</option>
            <option value="EUR" ${currency === "EUR" ? "selected" : ""}>EUR</option>
            <option value="GBP" ${currency === "GBP" ? "selected" : ""}>GBP</option>
          </select>
        </div>
      </section>
      <section class="result-section ecommerce-layout" aria-labelledby="results-title">
        <h2 id="results-title" class="section-title">Results</h2>
        <article class="result-card">
          <div class="result-icon"><i class="fa-solid fa-sack-dollar" aria-hidden="true"></i></div>
          <p class="result-value" data-testid="primary-value">${formatWholeCurrency(result.firstYearMonthlySavings, currency)}</p>
          <p class="result-label">First Year Monthly Savings</p>
        </article>
        <article class="result-card">
          <div class="result-icon"><i class="fa-solid fa-user-large" aria-hidden="true"></i></div>
          <p class="result-value">${formatDays(result.totalDaysSavedPerMonth)} days</p>
          <p class="result-label">Total Monthly CSR Time Saved</p>
          <p class="result-subline">${formatNumber(result.monthlyOnlineOrders)} online orders/month</p>
        </article>
      </section>
      <footer class="roi-footnotes">
        <span>* based on 21 business days per month and 8 hours per day</span>
        <span>* default values are based on industry averages and standard fees for the eCommerce solution</span>
      </footer>
      </main>
      <button type="button" class="help-trigger" data-action="open-help" aria-label="Show formula details">?</button>
      ${renderHelpModal(
        "eCommerce ROI Calculator - Computation",
        ecommerceHelpHtml(inputs, result, currency),
        helpOpen
      )}
    </div>
  `;
}
